import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useIdentity, personLabel } from '../context/IdentityContext';
import {
  EmailDraft,
  EmailDraftStatus,
  getEmailConfig,
  listEmailDrafts,
  patchEmailDraft,
  approveEmailDraft,
  rejectEmailDraft,
  deleteEmailDraft,
  completeTask,
} from '../services/api';

// ============================================================================
// Approval Queue — outbound email drafts waiting for a human's review.
//
// AI files email drafts here (weekly reports, proposals, team comms, donor
// outreach) instead of holding them ad-hoc in a chat. Nothing is sent until a
// human approves a draft on this page; approving sends it via Resend. Each card
// shows the recipient, subject, body, source task, and timestamps, with
// approve / edit / reject controls.
// ============================================================================

const fmtTime = (iso: string | null): string => {
  if (!iso) return '';
  try {
    const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z');
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

type QueueFilter = 'waiting' | 'sent' | 'rejected' | 'failed' | 'all';

// Each filter shows exactly one group, so sent mail is never mixed in with rejected drafts.
// "Waiting" also carries approved-but-unsent and failed sends: both still need a human.
const FILTERS: Record<QueueFilter, { label: string; match: (d: EmailDraft) => boolean; empty: string }> = {
  waiting: {
    label: 'Waiting for you',
    match: (d) => d.status === 'pending_review' || d.status === 'approved' || d.status === 'failed',
    empty: 'Nothing waiting for approval. When your AI drafts an email, it shows up here.',
  },
  sent: { label: 'Sent', match: (d) => d.status === 'sent', empty: 'No emails have been sent yet.' },
  rejected: { label: 'Rejected', match: (d) => d.status === 'rejected', empty: 'No rejected drafts.' },
  failed: { label: 'Failed', match: (d) => d.status === 'failed', empty: 'No failed sends.' },
  all: { label: 'All', match: () => true, empty: 'No email drafts yet.' },
};

const ApprovalQueue: React.FC = () => {
  const { theme } = useTheme();
  const { currentPerson } = useIdentity();
  // ?draft=<id> deep link. AI notices say "draft #214"; without this the human still has
  // to find it among every draft on the page, which is how #214 went unapproved on
  // 2026-09-08. With it the page opens on that draft, highlighted and scrolled to.
  const [searchParams] = useSearchParams();
  const focusIdRaw = searchParams.get('draft');
  const focusId = focusIdRaw && /^\d+$/.test(focusIdRaw) ? Number(focusIdRaw) : null;
  const focusRef = useRef<HTMLDivElement | null>(null);
  const scrolledForRef = useRef<number | null>(null);

  const [drafts, setDrafts] = useState<EmailDraft[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<QueueFilter>('waiting');
  const [config, setConfig] = useState<{ configured: boolean; reason: string | null } | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  // load() drives the visible spinner (manual refresh / first paint). The silent
  // flag is used by the background poll so newly filed drafts appear on their own
  // without flashing the "Loading…" state on every tick.
  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await listEmailDrafts();
      setDrafts(res.drafts);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    getEmailConfig().then(setConfig).catch(() => setConfig(null));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-refresh: a reviewer keeps this page open while AI files drafts, so poll
  // quietly every 12s. Without this, a draft filed after the page loaded never
  // surfaces until a manual refresh. The poll is silent (no spinner) and the
  // DraftCard only re-syncs fields when not mid-edit, so it never clobbers edits.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') load(true);
    }, 12000);
    return () => window.clearInterval(id);
  }, [load]);

  const focusDraft = focusId === null ? null : drafts.find((d) => d.id === focusId) || null;

  // A deep-linked draft must be visible even when it sits outside the current tab (an
  // already-sent or rejected one), so widen to "All" rather than showing an empty page.
  useEffect(() => {
    if (focusDraft && !FILTERS[filter].match(focusDraft)) setFilter('all');
  }, [focusDraft, filter]);

  // Scroll to the deep-linked card once, after it has actually rendered.
  useEffect(() => {
    if (!focusId || !focusRef.current || scrolledForRef.current === focusId) return;
    scrolledForRef.current = focusId;
    focusRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focusId, focusDraft, filter]);

  const pending = drafts.filter((d) => FILTERS.waiting.match(d));
  const visible = drafts.filter((d) => FILTERS[filter].match(d));
  const pendingCount = pending.length;
  const countFor = (key: QueueFilter) => drafts.filter((d) => FILTERS[key].match(d)).length;

  // ---- styles --------------------------------------------------------------
  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.surface,
    border: `1px solid ${theme.border}`,
    borderRadius: '10px',
    padding: '16px',
    marginBottom: '14px',
  };
  const btn = (bg: string): React.CSSProperties => ({
    backgroundColor: bg,
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '7px',
    padding: '8px 14px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  });
  const ghostBtn: React.CSSProperties = {
    backgroundColor: 'transparent',
    color: theme.textSecondary,
    border: `1px solid ${theme.border}`,
    borderRadius: '7px',
    padding: '8px 14px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  };
  const inputStyle: React.CSSProperties = {
    backgroundColor: theme.background,
    color: theme.text,
    border: `1px solid ${theme.border}`,
    borderRadius: '6px',
    padding: '6px 8px',
    fontSize: '13px',
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '24px 16px' }}>
      <h1 style={{ color: theme.text, margin: 0 }}>Approval Queue</h1>
      <p style={{ color: theme.textSecondary, margin: '6px 0 0', fontSize: '14px', maxWidth: '760px' }}>
        Email drafts your AI prepared (weekly reports, proposals, team comms, outreach) wait here for
        your review. Read each one, edit if needed, then approve to send or reject. Nothing goes out
        until you approve it.
      </p>

      {config && !config.configured && (
        <div
          style={{
            ...cardStyle,
            borderColor: theme.warning,
            marginTop: '16px',
            color: theme.text,
            fontSize: '13px',
          }}
        >
          <strong style={{ color: theme.warning }}>Sending not wired up yet.</strong>{' '}
          {config.reason} You can still review, edit, and approve drafts; an approved draft will be
          held until the Resend key is available, then it can be approved again to go out.
        </div>
      )}

      {/* Controls */}
      <div
        style={{
          ...cardStyle,
          marginTop: '16px',
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ color: theme.text, fontSize: '14px', fontWeight: 600 }}>
          {pendingCount} draft{pendingCount === 1 ? '' : 's'} waiting for you
        </span>
        <button onClick={() => load()} disabled={loading} style={{ ...ghostBtn, marginLeft: 'auto' }}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Filter: one status group at a time, so sent mail is never mixed in with rejects */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '12px' }}>
        {(Object.keys(FILTERS) as QueueFilter[]).map((key) => {
          const active = key === filter;
          return (
            <button
              key={key}
              onClick={() => setFilter(key)}
              style={{
                ...ghostBtn,
                padding: '6px 12px',
                backgroundColor: active ? theme.primary : 'transparent',
                color: active ? '#FFFFFF' : theme.textSecondary,
                borderColor: active ? theme.primary : theme.border,
              }}
            >
              {FILTERS[key].label} ({countFor(key)})
            </button>
          );
        })}
      </div>

      {focusId !== null && !loading && !focusDraft && (
        <div style={{ ...cardStyle, marginTop: '12px', borderColor: theme.warning, color: theme.text, fontSize: '13px' }}>
          Draft #{focusId} is not in this queue. It may have been deleted, or you may be signed in
          to a different cockpit. Everything that exists is listed under the tabs above.
        </div>
      )}

      {banner && <div style={{ ...cardStyle, color: theme.text, fontSize: '13px' }}>{banner}</div>}

      {/* Drafts */}
      {loading && drafts.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', color: theme.textSecondary }}>Loading…</div>
      ) : visible.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', color: theme.textSecondary }}>
          {FILTERS[filter].empty}
        </div>
      ) : (
        <div style={{ marginTop: '14px' }}>
          {visible.map((d) => (
            <DraftCard
              key={d.id}
              draft={d}
              focused={d.id === focusId}
              focusRef={d.id === focusId ? focusRef : undefined}
              theme={theme}
              cardStyle={cardStyle}
              btn={btn}
              ghostBtn={ghostBtn}
              inputStyle={inputStyle}
              canSend={!!config?.configured}
              approverName={personLabel(currentPerson)}
              onChanged={load}
              setBanner={setBanner}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ---- one draft card --------------------------------------------------------

// What a failed request means to the person reading the queue. The server's own
// `detail` (a 409 "already sent" / "already being sent", a 403 "sign in as a human")
// is the best sentence available; a request that never got an answer needs the one
// warning that matters here, that the email may still have gone out, so the reviewer
// refreshes instead of approving it a second time.
const serverDetail = (err: unknown): { status?: number; detail?: string } => {
  const e = err as { response?: { status?: number; data?: { detail?: unknown } }; message?: string };
  const raw = e?.response?.data?.detail;
  const detail = typeof raw === 'string' ? raw : undefined;
  return { status: e?.response?.status, detail };
};

export const explainApproveError = (err: unknown, to: string): string => {
  const { status, detail } = serverDetail(err);
  if (status === undefined) {
    return `The send request to ${to} was interrupted before the server answered. The email may still have gone out: check the Sent filter before approving it again.`;
  }
  if (detail) return detail;
  return `Could not send the email (server answered ${status}).`;
};

export const explainRequestError = (err: unknown, what: string): string => {
  const { status, detail } = serverDetail(err);
  if (detail) return detail;
  if (status === undefined) return `Could not ${what}: the request never reached the server. Check your connection and try again.`;
  return `Could not ${what} (server answered ${status}).`;
};

const STATUS_META: Record<EmailDraftStatus, { label: string; key: 'success' | 'warning' | 'error' | 'primary' | 'textSecondary' }> = {
  pending_review: { label: 'Waiting for you', key: 'primary' },
  approved: { label: 'Sending…', key: 'warning' },
  sent: { label: 'Sent', key: 'success' },
  failed: { label: 'Send failed', key: 'error' },
  rejected: { label: 'Rejected', key: 'textSecondary' },
};

interface DraftCardProps {
  draft: EmailDraft;
  theme: any;
  cardStyle: React.CSSProperties;
  btn: (bg: string) => React.CSSProperties;
  ghostBtn: React.CSSProperties;
  inputStyle: React.CSSProperties;
  canSend: boolean;
  approverName: string;
  onChanged: () => void;
  setBanner: (s: string | null) => void;
  // Set when this card is the one named by ?draft=<id>: it gets an accent outline and
  // the page scrolls to it.
  focused?: boolean;
  focusRef?: React.MutableRefObject<HTMLDivElement | null>;
}

const DraftCard: React.FC<DraftCardProps> = ({
  draft,
  theme,
  cardStyle,
  btn,
  ghostBtn,
  inputStyle,
  canSend,
  approverName,
  onChanged,
  setBanner,
  focused,
  focusRef,
}) => {
  const [to, setTo] = useState(draft.to_addrs);
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [taskCompleted, setTaskCompleted] = useState(false);
  const [deleted, setDeleted] = useState(false);

  // Re-sync from server when not mid-edit.
  useEffect(() => {
    if (!dirty) {
      setTo(draft.to_addrs);
      setSubject(draft.subject);
      setBody(draft.body);
    }
  }, [draft.to_addrs, draft.subject, draft.body, dirty]);

  const meta = STATUS_META[draft.status];
  const labelColor = (theme as any)[meta.key] || theme.textSecondary;
  const editable = draft.status === 'pending_review' || draft.status === 'failed';

  const save = async () => {
    setBusy(true);
    setBanner(null);
    try {
      await patchEmailDraft(draft.id, { to_addrs: to, subject, body });
      setDirty(false);
      setEditing(false);
      onChanged();
    } catch (err) {
      setBanner(explainRequestError(err, 'save the edits'));
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    setBusy(true);
    setBanner(null);
    try {
      if (dirty) await patchEmailDraft(draft.id, { to_addrs: to, subject, body });
      const res = await approveEmailDraft(draft.id, approverName);
      if (!res.ok) {
        setBanner(res.reason || 'Could not send the email.');
      } else if (res.task_completed) {
        setTaskCompleted(true);
        setBanner(`Sent to ${to}. Task #${res.task_completed} marked complete.`);
      } else {
        setBanner(`Sent to ${to}.`);
      }
      setDirty(false);
      setEditing(false);
      onChanged();
    } catch (err) {
      // Before this catch, a failed approve was an unhandled rejection: the console got
      // an AxiosError and the page said nothing, so the reviewer clicked again. On
      // 2026-09-20 that second click hit "already sent" (409) because the FIRST click
      // had gone out fine; only its response was lost on a slow connection.
      setBanner(explainApproveError(err, to));
      // Whatever happened, the list is the truth: if the send did complete, the card
      // moves to Sent on this refresh instead of inviting another click.
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    setBusy(true);
    setBanner(null);
    try {
      await rejectEmailDraft(draft.id);
      onChanged();
    } catch (err) {
      setBanner(explainRequestError(err, 'reject this draft'));
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete this draft ("${draft.subject}")? This cannot be undone.`)) return;
    setBusy(true);
    setBanner(null);
    try {
      await deleteEmailDraft(draft.id);
      setDeleted(true);
      setBanner(`Deleted draft "${draft.subject}".`);
      onChanged();
    } catch (err) {
      setBanner(explainRequestError(err, 'delete this draft'));
    } finally {
      setBusy(false);
    }
  };

  if (deleted) return null;

  const metaLine: string[] = [];
  if (draft.cc_addrs) metaLine.push(`Cc ${draft.cc_addrs}`);
  if (draft.bcc_addrs) metaLine.push(`Bcc ${draft.bcc_addrs}`);
  if (draft.from_addr) metaLine.push(`From ${draft.from_addr}`);

  return (
    <div
      ref={focusRef}
      style={
        focused
          ? { ...cardStyle, borderColor: theme.primary, boxShadow: `0 0 0 2px ${theme.primary}55` }
          : cardStyle
      }
    >
      {focused && (
        <div style={{ color: theme.primary, fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>
          This is draft #{draft.id}, the one your email linked to.
        </div>
      )}
      {/* Header: subject + status chip + timestamp */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px', gap: '10px', flexWrap: 'wrap' }}>
        <h3 style={{ color: theme.text, margin: 0, fontSize: '16px', flex: '1 1 auto' }}>
          {editing && editable ? (
            <input
              value={subject}
              onChange={(e) => {
                setSubject(e.target.value);
                setDirty(true);
              }}
              style={{ ...inputStyle, width: '100%', fontSize: '15px', fontWeight: 600 }}
            />
          ) : (
            draft.subject
          )}
        </h3>
        <span
          style={{
            fontSize: '12px',
            fontWeight: 600,
            color: labelColor,
            border: `1px solid ${labelColor}`,
            borderRadius: '999px',
            padding: '2px 10px',
            whiteSpace: 'nowrap',
          }}
        >
          {meta.label}
        </span>
      </div>

      {/* Recipient + provenance */}
      <div style={{ color: theme.textSecondary, fontSize: '12px', marginBottom: '10px' }}>
        {editing && editable ? (
          <div style={{ marginBottom: '6px' }}>
            <span style={{ marginRight: '6px' }}>To</span>
            <input
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setDirty(true);
              }}
              style={{ ...inputStyle, width: '320px', maxWidth: '100%' }}
            />
          </div>
        ) : (
          <span style={{ color: theme.text }}>To {draft.to_addrs}</span>
        )}
        {metaLine.length > 0 && <span> · {metaLine.join(' · ')}</span>}
        <span>
          {' '}· {personLabel(draft.person)}'s AI
          {draft.source_task_id ? ` · from task #${draft.source_task_id}` : ''}
          {draft.created_at ? ` · drafted ${fmtTime(draft.created_at)}` : ''}
          {draft.sent_at ? ` · sent ${fmtTime(draft.sent_at)}` : ''}
        </span>
      </div>

      {draft.context && (
        <div
          style={{
            color: theme.textSecondary,
            fontSize: '12px',
            fontStyle: 'italic',
            marginBottom: '10px',
            paddingLeft: '10px',
            borderLeft: `2px solid ${theme.border}`,
          }}
        >
          {draft.context}
        </div>
      )}

      {/* Body */}
      {editing && editable ? (
        <textarea
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
            setDirty(true);
          }}
          rows={Math.min(24, Math.max(6, Math.ceil((body?.length || 0) / 80)))}
          style={{ ...inputStyle, width: '100%', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
        />
      ) : (
        <div
          style={{
            color: theme.text,
            fontSize: '14px',
            whiteSpace: 'pre-wrap',
            backgroundColor: theme.background,
            border: `1px solid ${theme.border}`,
            borderRadius: '8px',
            padding: '12px',
            maxHeight: '320px',
            overflowY: 'auto',
          }}
        >
          {draft.body}
        </div>
      )}

      {draft.error && (
        <div style={{ color: theme.error, fontSize: '12px', marginTop: '10px' }}>{draft.error}</div>
      )}
      {draft.status === 'rejected' && draft.rejected_reason && (
        <div style={{ color: theme.textSecondary, fontSize: '12px', marginTop: '10px' }}>
          Rejected: {draft.rejected_reason}
        </div>
      )}

      {/* Actions */}
      {draft.status === 'sent' ? (
        <div style={{ marginTop: '12px' }}>
          <div style={{ color: theme.success, fontSize: '13px', fontWeight: 600 }}>
            Sent{draft.resend_id ? ` (id ${draft.resend_id})` : ''}.
          </div>
          {draft.source_task_id && !taskCompleted && (
            <button
              onClick={async () => {
                try {
                  await completeTask(draft.source_task_id!);
                  setTaskCompleted(true);
                  setBanner(`Task #${draft.source_task_id} marked complete.`);
                } catch {
                  setBanner(`Could not mark task #${draft.source_task_id} complete.`);
                }
              }}
              style={{
                marginTop: '8px',
                padding: '6px 12px',
                backgroundColor: theme.success,
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Mark task #{draft.source_task_id} complete
            </button>
          )}
          {taskCompleted && (
            <div style={{ color: theme.success, fontSize: '12px', marginTop: '6px' }}>
              Task #{draft.source_task_id} marked complete.
            </div>
          )}
        </div>
      ) : (
        editable && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
            <button
              onClick={approve}
              disabled={busy || !canSend}
              title={canSend ? '' : 'Sending not configured yet'}
              style={{
                ...btn(theme.success),
                opacity: canSend ? 1 : 0.5,
                cursor: canSend ? 'pointer' : 'not-allowed',
              }}
            >
              Approve &amp; send
            </button>
            {editing ? (
              <button onClick={save} disabled={busy || !dirty} style={btn(theme.primary)}>
                Save edits
              </button>
            ) : (
              <button onClick={() => setEditing(true)} disabled={busy} style={ghostBtn}>
                Edit
              </button>
            )}
            <button onClick={reject} disabled={busy} style={ghostBtn}>
              Reject
            </button>
            <button onClick={remove} disabled={busy} style={{ ...ghostBtn, color: theme.error, marginLeft: 'auto' }}>
              Delete
            </button>
          </div>
        )
      )}
      {(draft.status === 'rejected' || draft.status === 'approved') && (
        <div style={{ display: 'flex', marginTop: '12px' }}>
          <button onClick={remove} disabled={busy} style={{ ...ghostBtn, color: theme.error, marginLeft: 'auto' }}>
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

export default ApprovalQueue;
